// SettingsModal.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import memberstack from '../memberstack';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Divider, Typography, Box, Stack, Grid, Paper, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { deleteUser, updateUserDetails } from '../actions/user';

const Row = ({ label, value }) => (
  <Stack
    direction="row"
    alignItems="baseline"
    justifyContent="space-between"
    sx={{
      py: 1.25,
      gap: 2,
      borderBottom: '1px dashed #eee',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary', letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500, textAlign: 'right' }}>
      {value || '—'}
    </Typography>
  </Stack>
);

const Section = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, md: 3 },
      borderRadius: 2,
      border: '1px solid #eaeaea',
      background: '#fafafa',
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const SettingsModal = ({ open, onClose, profileData = {} }) => {
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const member = useSelector((s) => s.user);

  // display-only state (account owner)
  const [firstName, setFirstName]       = useState('');
  const [lastName, setLastName]         = useState('');
  const [email, setEmail]               = useState('');

  // editable pharmacy details
  const [pharmacyName, setPharmacyName] = useState('');
  const [address, setAddress]           = useState('');
  const [city, setCity]                 = useState(''); 
  const [tel, setTel]                   = useState('');

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');
  const [saving, setSaving] = useState(false);

  const access = useSelector((s) => s.access);

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  // show date only (Sri Lanka-friendly)
  return d.toLocaleDateString("en-LK", { year: "numeric", month: "short", day: "2-digit" });
};

const statusLabel = (s) => {
  switch (s) {
    case "active": return "Active";
    case "pending": return "Pending";
    case "failed": return "Payment failed";
    case "cancelled": return "Cancelled";
    default: return "—";
  }
};


  useEffect(() => {
    if (!open) {
      setConfirmDelete(false);
      setDeleteErr('');
    }
  }, [open]);

  useEffect(() => {
    setFirstName(
      profileData.firstName || profileData?.customFields?.['first-name'] || ''
    );
    setLastName(
      profileData.lastName || profileData?.customFields?.['last-name'] || ''
    );
    setEmail(profileData.email || profileData?.auth?.email || '');

    setPharmacyName(profileData?.customFields?.['pharmacy-name'] || profileData?.pharmacyName || '');
    setAddress(profileData?.customFields?.['address'] || profileData?.address || '');
    setCity(profileData?.customFields?.['city'] || profileData?.city || '');
    setTel(profileData?.customFields?.['phone-number'] || profileData?.tel || '');

  }, [profileData]);


const handleSavePharmacyDetails = async () => {
  try {
    setSaving(true);

    const updatedUser = await dispatch(updateUserDetails({
      pharmacyName: pharmacyName.trim(),
      address: address.trim(),
      city: city.trim(),
      tel: tel.trim(),
    }));

    if (updatedUser) {
      const cf = updatedUser.customFields || {};

      setPharmacyName(cf['pharmacy-name'] ?? updatedUser.pharmacyName ?? '');
      setAddress(cf['address'] ?? updatedUser.address ?? '');
      setCity(cf['city'] ?? updatedUser.city ?? '');
      setTel(cf['phone-number'] ?? updatedUser.tel ?? '');
    }
  } catch (e) {
    console.error("Save pharmacy details failed:", e);
  } finally {
    setSaving(false);
  }
};



  const handleDelete = async () => {
    const result = await dispatch(
      deleteUser({ memberstackId: member?.id || member?.memberstackId })
    );
    if (result.ok) {
      await memberstack.logout();
      navigate('/');
    } else {
      setDeleteErr(result.msg || 'Could not delete account.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      scroll="paper"
      slotProps={{ paper: { sx: { borderRadius: '20px' } } }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Settings
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#fff' }}>
        <Grid container spacing={2.5}>
          {/* Account owner (read-only) */}
          <Grid item style={{width:'100%'}}>
            <Section title="Account owner">
              <Row label="First name" value={firstName} />
              <Row label="Last name"  value={lastName} />
              <Row label="Email"      value={email} />
            </Section>
          </Grid>

          {/* Pharmacy details (editable) */}
          <Grid item xs={12}>
            <Section title="Pharmacy details">
              <TextField
                fullWidth
                label="Pharmacy name"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& fieldset': {
                    borderColor: '#dadada',
                    borderRadius: '10px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 12px',
                  },
                }}
                value={pharmacyName}
                onChange={(e) => setPharmacyName(e.target.value)}
              />

              <TextField
                fullWidth
                label="Address"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& fieldset': {
                    borderColor: '#dadada',
                    borderRadius: '10px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 12px',
                  },
                }}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& fieldset': {
                    borderColor: '#dadada',
                    borderRadius: '10px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 12px',
                  },
                }}
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />

              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& fieldset': {
                    borderColor: '#dadada',
                    borderRadius: '10px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 12px',
                  },
                }}
                value={tel}
                onChange={(e) => setTel(e.target.value)}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSavePharmacyDetails}
                  disabled={saving}
                  sx={{
                    textTransform: 'capitalize',
                    borderRadius: '999px',
                    boxShadow: 'none',
                  }}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </Box>
            </Section>
          </Grid>

          <Grid item style={{ width: "100%" }}>
          <Section title="Subscription">
            <Row label="Status" value={statusLabel(access?.subscriptionStatus)} />
            <Row label="Next payment date" value={formatDate(access?.nextBilling)} />

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                To change your payment method, wait until the end of your billing period and resubscribe using the new payment method.
              </Typography>
            </Box>
          </Section>
        </Grid>


          {/* Delete account */}
          <Grid item style={{width:'100%'}}>
            <Section title="Delete Account">
              <Typography variant="body2" sx={{ mb: 2 }}>
                Deleting your account will automatically cancel your plan.
              </Typography>

              {deleteErr && (
                <Typography component="p" color="error" sx={{ mb: 2 }}>
                  {deleteErr}
                </Typography>
              )}

              {!confirmDelete ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => setConfirmDelete(true)}
                    sx={{
                      textTransform: 'capitalize',
                      borderRadius: '999px',
                      boxShadow: 'none',
                    }}
                  >
                    Delete Account
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: 0.5,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #f5c2c7',
                    bgcolor: '#fff5f5',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Are you sure you want to delete your account?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    This action <strong>cannot be undone</strong>. Your subscription will be cancelled and your data removed.
                  </Typography>

                  <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => setConfirmDelete(false)}
                      sx={{ textTransform: 'capitalize', borderRadius: '999px' }}
                    >
                      No, keep my account
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleDelete}
                      sx={{
                        textTransform: 'capitalize',
                        borderRadius: '999px',
                        boxShadow: 'none',
                      }}
                    >
                      Yes, delete my account
                    </Button>
                  </Stack>
                </Box>
              )}
            </Section>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;

